<style lang="scss">
  .scrollArea {
    height: 34rem;
    overflow-y: auto;

    #liveFeed {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      height: 100%;
    }

    @media (max-width: 1300px) {
      height: 56rem;
    }
  }

  .liveFeedPagination {
    display: flex;
    justify-content: center;

    button.loadMore {
      padding: 0.4rem 0.8rem;
      background-color: #ffffff;
      border: 2px solid #e7e9f0;
      border-radius: 4px;
      font-size: 0.9rem;

      &:not(:disabled) {
        &:hover {
          cursor: pointer;
          background-color: #e8f7f5;
          color: #2a4881;
        }
      }

      &:disabled {
        background-color: #e7e9f0;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px #0091d9;
      }
    }
  }

  #noActivityText {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    text-align: center;

    p {
      margin-bottom: 0;
      a {
        color: #049bd7;
        &:hover {
          color: #4e73df;
        }
      }
    }
  }
</style>

<script>
  import { getContext } from "svelte";
  import SkeletonLoading from "../../../components/legacy/SkeletonLoading.svelte";
  import { openTodosModal } from "../stores";
  import { getLiveFeedData } from "../utils/apiCalls";
  import { liveFeedRefreshButtonState, refreshLiveFeed } from "../utils/stores";
  import { users } from "../utils/usersAndGroups";
  import { arraysHaveSameStrings } from "../utils/utils";
  import LiveFeedItem from "./LiveFeedItem.svelte";
  import LiveFeedNoActivity from "./LiveFeedNoActivity.svelte";

  export let kinds = ["clicks", "hotlist", "opens", "replies", "voicemails"];
  export let selectedUsers = [];

  const liveFeedLength = 25;
  const campaignTypeStore = getContext("selectedCampaignType");

  let data;

  let selectedKinds = kinds;

  let userIds = [];
  let selectedUserIds = selectedUsers.map((user) => user.value);

  let campaignType = $campaignTypeStore.value;
  let selectedCampaignType = campaignType;
  $: campaignType = $campaignTypeStore.value;

  let liveFeedData = [];
  let isLoading = true;

  let selectedCandidateId = null;

  let scrollArea;
  let oldestTimestamp = Date.now() / 1000;
  let hasMoreData = true;
  let isInitialLoad = true;

  let isRefreshing = false;

  const resetLiveFeed = () => {
    liveFeedData = [];
    oldestTimestamp = Date.now() / 1000;
    hasMoreData = true;
    isInitialLoad = true;
  };

  const fetchLiveFeedData = async (payload) => {
    if (!data.userIds.length) {
      resetLiveFeed();
      isLoading = false;
      isRefreshing = false;
      liveFeedRefreshButtonState.set("afterLoad");
      return;
    }
    try {
      isLoading = true;
      const response = await getLiveFeedData(payload);
      const newEvents = response.data.events;

      if (!response.data.moreData || !newEvents.length) hasMoreData = false;

      if (newEvents.length > 0) {
        oldestTimestamp = newEvents.at(-1).timestamp;
        liveFeedData = isInitialLoad
          ? newEvents
          : [...liveFeedData, ...newEvents];
        isInitialLoad = false;
      }
    } finally {
      isLoading = false;
      isRefreshing = false;
      liveFeedRefreshButtonState.set("afterLoad");
    }
  };

  const handleLoadMore = () =>
    fetchLiveFeedData({
      ...data,
      before: oldestTimestamp,
    });

  const handleCandidateClick = (event) => {
    selectedCandidateId = event.detail.candidateId;
    openTodosModal.set({ candidateId: selectedCandidateId }, true);
  };

  $: {
    userIds = selectedUsers.map((user) => user.value);

    const filtersChanged =
      !arraysHaveSameStrings(userIds, selectedUserIds) ||
      !arraysHaveSameStrings(kinds, selectedKinds) ||
      campaignType !== selectedCampaignType;

    if (filtersChanged) {
      resetLiveFeed();
      selectedUserIds = userIds;
      selectedKinds = kinds;
      selectedCampaignType = campaignType;
    }

    data = {
      before: oldestTimestamp,
      length: liveFeedLength,
      draw: 2,
      campaignType: campaignType === "allCampaigns" ? "_all" : campaignType,
      userIds,
      ...Object.fromEntries(kinds.map((kind) => [kind, true])),
    };

    if (isInitialLoad || filtersChanged) fetchLiveFeedData(data);
  }

  $: {
    if ($refreshLiveFeed && !isRefreshing) {
      isRefreshing = true;

      resetLiveFeed();

      userIds = selectedUsers.length
        ? selectedUsers.map((user) => user.value)
        : users.map((user) => user.value);

      data = {
        before: Date.now() / 1000,
        length: liveFeedLength,
        draw: 2,
        campaignType: campaignType === "allCampaigns" ? "_all" : campaignType,
        userIds,
        ...Object.fromEntries(kinds.map((kind) => [kind, true])),
      };

      fetchLiveFeedData(data);
      refreshLiveFeed.set(false);
    }
  }

  const getUniqueKey = (event, index) => `${event.id || ""}-${index}`;
</script>

<div class="scrollArea" bind:this={scrollArea}>
  <div id="liveFeed">
    {#each liveFeedData as event, index (getUniqueKey(event, index))}
      <LiveFeedItem {event} on:candidateClick={handleCandidateClick} />
    {/each}
    {#if isLoading}
      <SkeletonLoading
        containerStyles={{ gap: "1rem" }}
        itemStyles={{ height: "2.8125rem", borderRadius: "0.375rem" }}
        itemCount={liveFeedData.length ? 5 : 9}
      />
    {:else if !liveFeedData.length || !selectedUsers.length}
      <LiveFeedNoActivity>
        <div id="noActivityText">
          <p>You have no activity on your live feed</p>
          {#if !selectedUsers.length}
            <p>Select a user from the dropdown menu</p>
          {:else}
            <p>
              Try <a
                href="/campaigns"
                target="_blank"
                style="font-size: unset !important">launching a campaign</a
              >
            </p>
          {/if}
        </div>
      </LiveFeedNoActivity>
    {:else if hasMoreData}
      <div class="liveFeedPagination">
        <button class="loadMore" on:click={handleLoadMore} disabled={isLoading}
          >Load More</button
        >
      </div>
    {/if}
  </div>
</div>
